/** Deterministic fixtures for the screenshot scenes (I15).
 *
 * A visual critic judges stills, so a still has to be the same picture every
 * time. Everything here is a pure function of a fixed seed and the shipped
 * content bundle: no clock, no `Math.random`, no live game. Where a screen needs
 * simulation state the fixture builds the plain-data shape directly rather than
 * playing a game into it, because a game that has to be played to reach a screen
 * is a game whose screen cannot be reviewed until the rules that reach it exist.
 */

import type {
  ArmySlot, ArmyState, ArtifactDefId, BattleState, BuildingDefId, ContentRegistry,
  CreatureDefId, HeroDef, HeroState, Obstacle, SpellDefId, TownState,
} from "@game/core";
import {
  asArmyId, asFactionId, asHeroId, asPlayerId, asTownId,
  FIELD_OBSTACLE_STREAM, fieldObstacles, seedStream, startBattle,
} from "@game/core";
import { fixtureSiegeObstacles } from "@game/render";
import { TERRAINS } from "@game/core";
import type { GameState } from "@game/core";
import { townName } from "./newgame.js";

const P1 = asPlayerId("p1");
const P2 = asPlayerId("p2");

/** Sorted so a fixture never depends on a map's insertion order. */
function sortedIds<T extends string>(ids: Iterable<T>): T[] {
  return [...ids].sort();
}

// --------------------------------------------------------------------- town

/** Every building the faction has, in the order the content declares them, so
 *  a town screen scene shows the finished town rather than a building site. */
/**
 * The cell the `adventure-ridge` scene centres on: the most photogenic stretch
 * of mountain wall on the surface.
 *
 * A wall is judged by what the rounding work has to prove - a run of rock long
 * enough to show both END CAPS and the interior between them, with the wall
 * turning north or south somewhere along it so a BEND is in the frame too.
 * The longest such run wins, bends counting for four cells each; a map with no
 * run of three at all falls back to the hero's own start, which is what the
 * plain adventure scene shows.
 */
export function ridgeFixture(
  state: GameState,
): { readonly x: number; readonly y: number; readonly z: number } | null {
  const m = state.map;
  const w = m.width, h = m.height;
  // A wall cell is rock, or a cell wearing a range piece: the dressing puts
  // the surrounding ground back under every piece it stands, so the wall is
  // read off the pieces and the rock is only what it failed to dress.
  const wallAt = new Set<number>();
  for (const o of Object.values(state.objects)) {
    if (o.z === 0 && (o.def as string).startsWith("obs_mtn_")) wallAt.add(o.y * w + o.x);
  }
  const rock = (x: number, y: number): boolean =>
    x >= 0 && y >= 0 && x < w && y < h
    && (TERRAINS[m.terrain[y * w + x]!] === "rock" || wallAt.has(y * w + x));
  let best: { score: number; x: number; y: number } | null = null;
  for (let y = 0; y < h; y++) {
    let x = 0;
    while (x < w) {
      if (!rock(x, y)) { x++; continue; }
      let end = x;
      while (end < w && rock(end, y)) end++;
      const len = end - x;
      // Runs that touch the map edge are the border wall, half of which is
      // off the map; the first pick was one at x=0 and the frame was mostly
      // the west edge of the world.
      if (len >= 3 && x > 0 && end < w && y > 1 && y < h - 2) {
        let bends = 0;
        for (let k = x; k < end; k++) {
          if (rock(k, y - 1) || rock(k, y + 1)) bends++;
        }
        // Count the bend cells that sit at the run's ends, where they read as
        // a corner, twice as heavily as ones in the middle of a thick wall.
        const corner = Number(rock(x, y - 1) || rock(x, y + 1))
          + Number(rock(end - 1, y - 1) || rock(end - 1, y + 1));
        // And prefer a run with more wall around it in the viewport: a ridge
        // that carries on up the screen photographs as a range, a lone run of
        // three as a rock.
        let around = 0;
        for (let dy = -6; dy <= 6; dy++) {
          for (let dx = -10; dx <= 10; dx++) if (rock(x + dx, y + dy)) around++;
        }
        const score = len + 2 * Math.min(bends, 3) + 4 * corner + around / 6;
        if (best === null || score > best.score) {
          best = { score, x: x + Math.floor(len / 2), y };
        }
      }
      x = end;
    }
  }
  return best === null ? null : { x: best.x, y: best.y, z: 0 };
}

/**
 * The cell the `adventure-ridge-snow` scene centres on: the window of the map
 * holding the most SNOW mountain wall.
 *
 * The snow kit is the one the last verdict called out hardest ("1-cell
 * white-grey smudges with no peak"), and no scene framed it: `adventure-night`
 * centres on its hero, and the snowfields it opens on hold isolated rocks
 * rather than a range. Scored over a viewport-sized window rather than along a
 * row, because what has to be in frame is a MASS of snow wall, not one run.
 */
export function snowRidgeFixture(
  state: GameState,
): { readonly x: number; readonly y: number; readonly z: number } | null {
  return styleRidgeFixture(state, (def) => def.startsWith("obs_mtn_snow"), "snow");
}

/**
 * The same window search for the lava and the swamp kits.
 *
 * The item asks for rounded mountains on every terrain the map uses, and the
 * last verdict could only judge grass and snow because those were the only
 * two with a scene: "there is no in-game evidence for any of them". The
 * predicate is a substring rather than a prefix because those kits' ids do
 * not share one - a lava wall is drawn from `obs_mtn_lava_mass_*`,
 * `obs_mtn_skyreach_lava_*`, `obs_mtn_peak_lava_*` and
 * `obs_mtn_foot_lava_ridge_*`. Snow keeps the prefix test it had so its
 * scene frames the same window it framed last round and the two are
 * comparable.
 */
export function lavaRidgeFixture(
  state: GameState,
): { readonly x: number; readonly y: number; readonly z: number } | null {
  return styleRidgeFixture(state, (def) => def.includes("lava"), "lava");
}

export function swampRidgeFixture(
  state: GameState,
): { readonly x: number; readonly y: number; readonly z: number } | null {
  return styleRidgeFixture(state, (def) => def.includes("swamp"), "swamp");
}

function styleRidgeFixture(
  state: GameState, isKit: (def: string) => boolean, terrain: string,
): { readonly x: number; readonly y: number; readonly z: number } | null {
  const m = state.map;
  const w = m.width, h = m.height;
  const wall = new Set<number>();
  for (const o of Object.values(state.objects)) {
    const def = o.def as string;
    if (o.z === 0 && def.startsWith("obs_mtn_") && isKit(def)) wall.add(o.y * w + o.x);
  }
  if (wall.size === 0) return null;
  let best: { score: number; x: number; y: number } | null = null;
  for (let y = 8; y < h - 8; y += 2) {
    for (let x = 14; x < w - 14; x += 2) {
      let n = 0, ground = 0;
      for (let dy = -8; dy <= 8; dy++) {
        for (let dx = -14; dx <= 14; dx++) {
          const xx = x + dx, yy = y + dy;
          if (xx < 0 || yy < 0 || xx >= w || yy >= h) continue;
          if (wall.has(yy * w + xx)) n++;
          if (TERRAINS[m.terrain[yy * w + xx]!] === terrain) ground++;
        }
      }
      const score = n * 4 + Math.min(ground, 200) / 10;
      if (best === null || score > best.score) best = { score, x, y };
    }
  }
  return best === null ? null : { x: best.x, y: best.y, z: 0 };
}

/**
 * The cell the `adventure-waterways` scene centres on: the window of the map
 * that shows the most of what the eight-connected overlays exist to show.
 *
 * Scored over a viewport-sized window (`WATERWAYS_WIN` cells): road cells
 * whose join to the next cell is a corner, road cells that are a bend, river
 * cells, river cells on a corner join, and - weighted hardest, because a lava
 * field with a flow in it is the rarest of the four - lava-river cells. A
 * corner join is counted the way the renderer draws it: only when neither
 * flanking cell carries the overlay.
 */
const WATERWAYS_WIN = { w: 22, h: 13 } as const;

export interface WaterwaysStats {
  readonly diagRoad: number;
  readonly bendRoad: number;
  readonly river: number;
  readonly diagRiver: number;
  readonly lava: number;
  readonly ice: number;
  readonly water: number;
}

export function waterwaysFixture(
  state: GameState,
): { readonly x: number; readonly y: number; readonly z: number; readonly stats: WaterwaysStats } | null {
  const m = state.map;
  const w = m.width, h = m.height;
  const on = (p: Uint8Array, x: number, y: number): boolean =>
    x >= 0 && y >= 0 && x < w && y < h && p[y * w + x] !== 0;
  const links = (p: Uint8Array, x: number, y: number): [number, number][] => {
    const out: [number, number][] = [];
    for (const [dx, dy] of [[1, 0], [-1, 0], [0, 1], [0, -1]] as const) {
      if (on(p, x + dx, y + dy)) out.push([dx, dy]);
    }
    for (const [dx, dy] of [[1, 1], [1, -1], [-1, 1], [-1, -1]] as const) {
      if (on(p, x + dx, y + dy) && !on(p, x + dx, y) && !on(p, x, y + dy)) out.push([dx, dy]);
    }
    return out;
  };
  const isDiag = (p: Uint8Array, x: number, y: number): boolean =>
    links(p, x, y).some(([dx, dy]) => dx !== 0 && dy !== 0);
  const isBend = (p: Uint8Array, x: number, y: number): boolean => {
    const l = links(p, x, y);
    return l.length === 2 && (l[0]![0] !== -l[1]![0] || l[0]![1] !== -l[1]![1]);
  };
  const WATER = 1, ICE = 2, LAVA = 3;

  let best: { score: number; x: number; y: number; stats: WaterwaysStats } | null = null;
  const hw = Math.floor(WATERWAYS_WIN.w / 2), hh = Math.floor(WATERWAYS_WIN.h / 2);
  for (let cy = hh; cy < h - hh; cy++) {
    for (let cx = hw; cx < w - hw; cx++) {
      let diagRoad = 0, bendRoad = 0, river = 0, diagRiver = 0, lava = 0, ice = 0, water = 0;
      for (let y = cy - hh; y <= cy + hh; y++) {
        for (let x = cx - hw; x <= cx + hw; x++) {
          if (on(m.road, x, y)) {
            if (isDiag(m.road, x, y)) diagRoad++;
            if (isBend(m.road, x, y)) bendRoad++;
          }
          if (on(m.river, x, y)) {
            river++;
            if (isDiag(m.river, x, y)) diagRiver++;
            const k = m.river[y * w + x];
            if (k === LAVA) lava++;
            if (k === ICE) ice++;
            if (k === WATER) water++;
          }
        }
      }
      const score = Math.min(diagRoad, 8) * 2 + Math.min(bendRoad, 6) + Math.min(river, 14)
        + Math.min(diagRiver, 6) * 2 + Math.min(lava, 8) * 4 + Math.min(ice, 6) + Math.min(water, 10) * 2;
      if (best === null || score > best.score) {
        best = { score, x: cx, y: cy, stats: { diagRoad, bendRoad, river, diagRiver, lava, ice, water } };
      }
    }
  }
  return best === null ? null : { x: best.x, y: best.y, z: 0, stats: best.stats };
}

export function fullyBuilt(content: ContentRegistry, faction: string): readonly BuildingDefId[] {
  const f = content.factions.get(asFactionId(faction));
  if (f === undefined) return [];
  return sortedIds(f.buildings);
}

export function townFixture(content: ContentRegistry, faction: string): TownState {
  const f = content.factions.get(asFactionId(faction));
  const built = fullyBuilt(content, faction);

  // A dwelling with nothing waiting in it reads as a bug in a screenshot, so
  // each of the faction's creatures gets a plausible week's growth.
  const available: Record<string, number> = {};
  const creatures = f === undefined ? [] : sortedIds(f.creatures);
  creatures.forEach((id, i) => { available[id as string] = Math.max(1, 22 - i * 3); });

  const guild: SpellDefId[][] = [[], [], [], [], []];
  const spells = sortedIds(content.spells.keys());
  for (let level = 0; level < 5; level++) {
    for (let i = 0; i < (level < 2 ? 4 : 3); i++) {
      const pick = spells[(level * 11 + i * 7) % spells.length];
      if (pick !== undefined) guild[level]!.push(pick);
    }
  }

  return {
    id: asTownId("town:1"),
    faction: asFactionId(faction),
    owner: P1,
    // The town's own name, not its faction's. Every real the classic town
    // screen puts the name here; this fixture printed "Castle" over
    // "Castle" until 2026-08-29.
    name: townName(faction, 0, 0),
    x: 8, y: 8, z: 0,
    built,
    builtThisTurn: false,
    available,
    garrison: asArmyId("army:1"),
    visitingHero: null,
    garrisonHero: null,
    guildSpells: guild,
    modifiers: [],
  };
}

/** A seven-slot army of the faction's own roster, biggest tier last. */
export function armyFixture(
  content: ContentRegistry, faction: string, id = "army:1",
): ArmyState {
  const f = content.factions.get(asFactionId(faction));
  const roster = f === undefined ? [] : sortedIds(f.creatures);
  const slots: (ArmySlot | null)[] = [];
  // A garrison and a field army are not the same army. Giving both rows the
  // same seven stacks with the same seven counts makes the town bar read as
  // duplicated placeholder data and defeats the one job it has, which is
  // letting the player tell the two apart at a glance.
  const garrison = id === "army:1";
  const counts = garrison ? [112, 0, 46, 0, 21, 8, 0] : [58, 31, 24, 15, 9, 5, 2];
  for (let i = 0; i < 7; i++) {
    const def = roster[i];
    const count = counts[i] ?? 4;
    slots.push(def === undefined || count === 0 ? null : { def, count });
  }
  return { id: asArmyId(id), owner: P1, slots };
}

// --------------------------------------------------------------------- hero

function heroDefFor(content: ContentRegistry, faction: string): HeroDef {
  const ids = sortedIds(content.heroes.keys()).filter(
    (id) => (content.heroes.get(id)?.faction as string) === faction,
  );
  const first = ids[0];
  const def = first === undefined ? undefined : content.heroes.get(first);
  if (def === undefined) throw new Error(`no hero definition for ${faction}`);
  return def;
}

/**
 * More relics than one row of the pack holds.
 *
 * The strip is five pockets wide, and a fixture that carried exactly five said
 * nothing about the second row - which is where the phone sheet was hiding
 * every relic past the fifth. Eighteen is more than the desktop shelf holds
 * at 1080p (three rows of five), so the pager under the strip is in every
 * shot too - a fixture that fit on one page said nothing about the second.
 */
const PACK_FIXTURE_RELICS = 18;

/**
 * A level-14 hero with a full sheet: four primaries, eight skills, artifacts in
 * a spread of slots, a backpack and a spellbook. A blank hero sheet tells a
 * critic nothing about whether the sheet works.
 */
export function heroFixture(content: ContentRegistry, faction = "sunspire"): HeroState {
  const def = heroDefFor(content, faction);

  const skillIds = sortedIds(content.skills.keys()).slice(0, 8);
  const secondary = skillIds.map((s, i) => ({ def: s, level: ((i % 3) + 1) as 1 | 2 | 3 }));

  // The 22 artifacts with unique icons come first in id order, which is also
  // the set most likely to have a slot the doll knows about.
  const artIds = sortedIds(content.artifacts.keys());
  const artifacts: Record<string, ArtifactDefId | null> = {};
  const backpack: ArtifactDefId[] = [];
  for (const id of artIds) {
    const a = content.artifacts.get(id);
    if (a === undefined) continue;
    const slot = a.slot as string;
    if (slot.length > 0 && slot !== "backpack" && artifacts[slot] === undefined) {
      artifacts[slot] = id;
    } else if (backpack.length < PACK_FIXTURE_RELICS) {
      backpack.push(id);
    }
    if (Object.keys(artifacts).length >= 12 && backpack.length >= PACK_FIXTURE_RELICS) break;
  }

  const spellbook = sortedIds(content.spells.keys()).slice(0, 24);

  return {
    id: asHeroId("hero:1"),
    def: def.id,
    owner: P1,
    faction: asFactionId(faction),
    x: 9, y: 8, z: 0,
    facing: 2,
    level: 14,
    // Inside level 14's band (28,784 .. 34,140). A number past the level-15
    // threshold made the sheet render "46,000 / 34,140" on a full bar, which
    // reads as a broken widget rather than as a fixture that disagrees with
    // itself.
    experience: 31200,
    primary: { attack: 12, defense: 10, spellPower: 8, knowledge: 7 },
    secondary,
    movementLeft: 1420,
    mana: 62,
    army: asArmyId("army:2"),
    artifacts,
    backpack,
    spellbook,
    modifiers: [],
    boat: null,
    inTown: null,
    visited: [],
  };
}

// ------------------------------------------------------------------- battle

function sideSlots(
  content: ContentRegistry, faction: string, counts: readonly number[], picks?: readonly number[],
): (ArmySlot | null)[] {
  const f = content.factions.get(asFactionId(faction));
  const roster = f === undefined ? [] : sortedIds(f.creatures);
  const out: (ArmySlot | null)[] = [];
  for (let i = 0; i < 7; i++) {
    const at = picks === undefined ? i * 2 : picks[i] ?? i * 2;
    const def: CreatureDefId | undefined = roster[at] ?? roster[i];
    out.push(def === undefined || counts[i] === undefined ? null : { def, count: counts[i]! });
  }
  return out;
}

/**
 * The besieged Greenmantle garrison, by index into the faction's sorted roster:
 * glade runner, leafshot, oakwarden, windsteed, stonebeard, verdant dragon. The default
 * every-other pick lands the gold and the green dragon in consecutive slots,
 * and with `START_ROWS` two-row pitch two dragons two rows apart lay on top
 * of each other; one two-hex stack, in the middle rank, is what the siege
 * references show.
 */
// The dragon LAST, on row 10, with the dwarf - the shortest figure in the
// roster - on row 8 above it: on row 6 the dragon's body and tail ran under
// the oakwarden two rows in front and its plaque landed beside the oakwarden's
// knees, and with the pegasus on row 8 the dragon's head covered the horse's
// legs. A 2.7-pitch stack on a two-row pitch overlaps whatever stands one
// rank behind it; at the front rank nothing stands in front of it, and a
// one-pitch dwarf behind it clears its crest. a reference screenshot
// keeps its dragon in the front half of the wall with nothing below it.
const SIEGE_GARRISON: readonly number[] = [1, 13, 3, 9, 5, 8];

/** The open-field scene's seed; see the note on `obstacles` in `battleFixture`. */
const FIELD_SEED = 20262793;

export interface BattleFixture {
  readonly state: BattleState;
  readonly attacker: string;
  readonly defender: string;
}

/**
 * Two armies deployed on a real arena. `startBattle` does the placement, so the
 * fixture is the kernel's own opening position rather than a hand-placed
 * tableau that could not occur in play.
 */
export function battleFixture(
  content: ContentRegistry,
  opts: {
    readonly siege?: boolean;
    readonly seed?: number;
    /**
     * Which of the nineteen painted arenas to fight on. Defaults to the pair
     * this fixture has always used - dirt in the open field, grass under a
     * siege - so every shot taken before this option existed still reproduces.
     *
     * It exists because two battle scenes were not enough to measure the
     * renderer against. `paintGroundDetail` in `arena.ts` paints one hard-coded
     * sandy-tan palette over every terrain, and the composited ground is
     * `0.327 * plate + 87.2`, so a plate at luma 30 (lava) and one at 253
     * (snow) both pull toward the same tan. Both declared scenes sit near plate
     * luma 110, which is exactly where that defect is invisible.
     */
    readonly terrain?: string;
    /** The attacking faction. Defaults to castle; see the note below. */
    readonly attacker?: string;
  } = {},
): BattleFixture {
  const siege = opts.siege === true;
  // Castle by default, because every battle shot ever compared was taken with
  // it and changing the roster would invalidate all of them. An override is
  // for scenes that need a specific creature on the board - the caster scene
  // wants Tower, whose Mage and Windcaller are the only player-facing casters at a
  // tier a fixture reaches.
  const attacker = opts.attacker ?? "sunspire";
  const defender = siege ? "greenmantle" : "gravewatch";
  // An open field carries obstacles too. Handing `startBattle` an empty list
  // drew a board with nothing on it between the two armies, and the renderer
  // cannot invent them: what is painted has to be what the rules block on.
  //
  // The field scatter comes from the KERNEL's own generator, the one every real
  // open-field battle is now fought over, so the scene photographs the board a
  // player gets rather than the renderer's own second guess at one. It is still
  // a pure function of `opts.seed`, so every scene reproduces. This moved on
  // 2026-08-30: the previous board came from `fixtureFieldObstacles`, which
  // laid 11 isolated props at seed 20260823 where `fieldObstacles` lays 16 in
  // eight formations, so shots taken before that date are not comparable.
  //
  // **And again on 2026-09-08, from 20260825 to 20262793, for the spread.** At
  // 20260825 the five formations' centres sat at device x 850, 1115, 1150, 1165
  // and 1230 inside a field spanning 262..1657 - a critic measured every prop
  // on the board inside the middle 27% of it, so both armies started on
  // featureless dirt and every obstacle was stacked on the centreline gutter
  // where no unit stands for two rounds. 20262793 lays 15 cells in six
  // formations (none of them a single cell) whose centre columns are 3.3, 4.5,
  // 6.0, 7.5, 9.0 and 10.7 - evenly across the whole span the kernel allows -
  // over ten of the eleven rows. Enumerated over 4000 seeds against the same
  // filter the 2026-09-02 note describes plus the spread; it is the widest of
  // the seventeen that qualify. The kernel keeps columns 0-2 and 12-14 clear
  // (`FIELD_MIN_COL`), so 3.3..10.7 is the whole board a blocking obstacle may
  // stand on; what is left of the critic's note is scenery in the outer
  // columns, which is ground dress rather than obstacles.
  //
  // The 2026-09-02 note, kept because the seed it names is what earlier shots
  // were taken at: the default moved from 20260823 to 20260825 because the
  // generator lays 12-17 cells in formations of one to three, and the renderer
  // now draws a formation as ONE painted piece; at 20260823 that came out as
  // eight pieces, three of them single-cell props, on a board the classic would give
  // three to six. 20260825 lays 13 cells as five formations (3,3,3,2,2 - not
  // a single-cell prop among them), which is the count and the shape of the
  // reference boards (a reference screenshot 3, a reference screenshot
  // 4, a reference screenshot 5). Enumerated over 400 seeds; it is the first that
  // qualifies. Still the kernel's own generator, still one pure function of
  // the seed.
  const obstacles: readonly Obstacle[] = siege
    ? fixtureSiegeObstacles(3)
    : fieldObstacles(seedStream(opts.seed ?? FIELD_SEED, FIELD_OBSTACLE_STREAM))[0];

  const state = startBattle({
    attacker: {
      slots: sideSlots(content, attacker, [42, 26, 18, 12, 7, 4, 2]),
      hero: {
        id: asHeroId("hero:1"), owner: P1,
        attack: 9, defense: 7, spellPower: 6, knowledge: 5, mana: 40,
        spellbook: sortedIds(content.spells.keys()).slice(0, 10),
        army: asArmyId("army:2"),
      },
    },
    defender: {
      // Six stacks under siege, seven in the open field. `START_ROWS[7]` is
      // The classic's own 0,1,3,5,7,9,10, and the fixture's Greenmantle roster puts a
      // two-hex Verdant Wyrm in slots 5 and 6: on rows 9 and 10 the two
      // sprites lay almost fully on top of each other and a critic named the
      // pair as the siege defence's loudest tell. `START_ROWS[6]` is
      // 0,2,4,6,8,10 - every rank a full row apart, which is the spacing the
      // siege references show behind the wall.
      slots: siege
        ? sideSlots(content, defender, [38, 16, 10, 8, 24, 6], SIEGE_GARRISON)
        : sideSlots(content, defender, [38, 24, 16, 10, 6, 3, 1]),
      hero: {
        id: asHeroId("hero:2"), owner: P2,
        attack: 7, defense: 9, spellPower: 5, knowledge: 6, mana: 35,
        spellbook: [],
        army: asArmyId("army:3"),
      },
    },
    terrain: opts.terrain ?? (siege ? "grass" : "dirt"),
    siege: siege ? { active: true, fortLevel: 3, gateOpen: false } : null,
    obstacles,
    rng: opts.seed ?? FIELD_SEED,
  }, content);

  return { state, attacker, defender };
}

/**
 * The magic-ground scene's frame: the middle of the largest enchanted patch.
 *
 * Section 31 grounds are scattered in handfuls of cells and the generator puts
 * them wherever the land allows, so a screenshot aimed at the hero photographs
 * ordinary grass. The largest patch is the one worth looking at - it is the one
 * a player would actually notice from the map - and its centroid frames it with
 * its own edges in shot, which is where the wash has to read as a place rather
 * than as a tint on a tile.
 */
export function magicGroundFixture(
  state: GameState,
): { readonly x: number; readonly y: number; readonly z: number } | null {
  const m = state.map;
  const plane = m.width * m.height;
  const patches = new Map<string, { n: number; sx: number; sy: number; z: number }>();
  for (let z = 0; z < m.levels; z++) {
    for (let i = 0; i < plane; i++) {
      const slot = m.ground[z * plane + i] ?? 0;
      if (slot === 0) continue;
      const key = `${String(z)}:${String(slot)}`;
      const at = patches.get(key) ?? { n: 0, sx: 0, sy: 0, z };
      at.n += 1;
      at.sx += i % m.width;
      at.sy += Math.trunc(i / m.width);
      patches.set(key, at);
    }
  }
  let best: { n: number; sx: number; sy: number; z: number } | null = null;
  // Sorted, so two runs of the same seed frame the same patch.
  for (const key of [...patches.keys()].sort()) {
    const at = patches.get(key)!;
    if (best === null || at.n > best.n) best = at;
  }
  if (best === null) return null;
  return { x: Math.round(best.sx / best.n), y: Math.round(best.sy / best.n), z: best.z };
}
